import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useScheduleIssues } from "../api/use-issues";
import { isRunActive, useCreateRun, useLatestRun } from "../api/use-runs";
import { issueUnitCountsBySeverity } from "../lib/group-issues";
import { computeRunGate } from "../lib/preflight-gate";

interface RunSchedulerButtonProps {
  scheduleId: string;
}

/**
 * Header action that kicks off a scheduler run. The run now executes on a
 * background worker, so the button's "running" state and its progress readout
 * are driven by the *persisted* latest run (via {@link useLatestRun}), not local
 * component state — which means a page refresh mid-solve reconnects to the
 * in-progress run and keeps ticking. The percentage is the solver's true
 * objective/bound-gap progress (`progressPercent`), not a fabricated tween.
 *
 * Clicking the enabled button fires `useCreateRun` (which persists a Running
 * row and enqueues the solve); polling then flips the button through the run's
 * lifecycle. When a run the user started here completes, we navigate to the
 * Validation Issues tab (success) or toast (failure).
 *
 * Visual contract:
 *   - Disabled  → muted slate button. Reason via `aria-describedby` → sibling
 *     `sr-only` `<span>`.
 *   - Enabled (no warnings) → indigo/brand primary button.
 *   - Enabled (warnings) → primary button + amber chip with a triangle glyph.
 *   - Running   → disabled primary button with a spinner + "Running… {pct}%"
 *     where `pct` is the solver's real completion percentage.
 */
export function RunSchedulerButton({ scheduleId }: RunSchedulerButtonProps) {
  // Source the error/warning chips from the issue list (not the backend's
  // schedule.issueCounts), so they're student-weighted and reconcile with the
  // Validation Issues tab: a "could not place" course contributes its affected
  // students, every other issue contributes 1. Shares the page's query cache.
  const { data: issues } = useScheduleIssues(scheduleId);
  const gate = computeRunGate(
    issues ? issueUnitCountsBySeverity(issues) : undefined,
  );
  const reasonId = useId();
  const navigate = useNavigate();

  // Optional per-run solver time limit (seconds). 0 = use the configured default
  // (~10 min). Longer presets let an admin kick off an on-demand search — e.g.
  // 10 hours overnight — to see if the solver finds a better solution.
  const [timeLimitSeconds, setTimeLimitSeconds] = useState(0);

  const { data: latestRun } = useLatestRun(scheduleId);
  const activeRun =
    latestRun && isRunActive(latestRun.status) ? latestRun : undefined;

  // Remember that WE started a run in this tab, so we only auto-navigate to the
  // issues tab for a run the user actually kicked off here — not for one picked
  // up from a background completion while they're viewing another surface.
  const initiatedRef = useRef(false);

  const mutation = useCreateRun(scheduleId, {
    onError: () => {
      initiatedRef.current = false;
      toast.error("Run failed — see console for details.");
    },
  });

  // Drive completion side effects off the persisted status transition rather
  // than the mutation resolving (which now only means "queued").
  const latestStatus = latestRun?.status;
  const prevStatusRef = useRef<typeof latestStatus>(undefined);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = latestStatus;
    if (!initiatedRef.current || !latestStatus) return;
    const wasActive = prev !== undefined && isRunActive(prev);
    if (!wasActive) return;
    if (latestStatus === "Succeeded") {
      initiatedRef.current = false;
      navigate(`/schedules/${scheduleId}?tab=issues`);
    } else if (latestStatus === "Failed") {
      initiatedRef.current = false;
      toast.error("Run failed — see the run details for the reason.");
    }
  }, [latestStatus, scheduleId, navigate]);

  const isRunning = !!activeRun || mutation.isPending;
  // Solver-reported true percentage; 0 during the brief queue/startup window
  // before the first solver solution (or for the instant greedy engine).
  const pct = activeRun?.progressPercent ?? 0;

  const handleClick = () => {
    if (!gate.enabled || isRunning) return;
    initiatedRef.current = true;
    // 0 → send nothing so the backend uses its configured default.
    mutation.mutate(timeLimitSeconds || undefined);
  };

  // Running branch takes precedence over the gate so an in-flight run always
  // shows progress, even if outstanding issues would otherwise disable the
  // button. Primary background, spinner + true percent. Disabled to prevent
  // double-submit; `aria-live="polite"` announces progress without stealing
  // focus.
  if (isRunning) {
    return (
      <button
        type="button"
        disabled
        data-testid="schedule-run-button"
        aria-label="Running scheduler"
        className="bg-brand text-white rounded-md px-3 py-1.5 font-semibold text-[13px] flex items-center gap-1.5 cursor-wait ml-auto opacity-90"
      >
        <SpinnerIcon />
        <span data-testid="schedule-run-progress" aria-live="polite">
          Running… {pct}%
        </span>
      </button>
    );
  }

  // Disabled branch — gate blocked. Neutral styling.
  if (!gate.enabled) {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          disabled
          data-testid="schedule-run-button"
          title={gate.reason}
          aria-label="Run Scheduler"
          aria-describedby={reasonId}
          className="bg-surface-2 text-text-faint rounded-md px-3 py-1.5 font-medium text-[13px] flex items-center gap-1.5 cursor-not-allowed opacity-70 ml-auto"
        >
          <RunIcon />
          Run Scheduler
        </button>
        <span
          id={reasonId}
          data-testid="schedule-run-disabled-reason"
          className="sr-only"
        >
          {gate.reason}
        </span>
      </>
    );
  }

  // Enabled branch — indigo/brand primary, optional error + warning chips.
  // Errors don't block the run (best-effort); the chip just flags that the
  // items behind them will be skipped.
  const labelParts: string[] = [];
  if (gate.errorBadge !== undefined) {
    labelParts.push(
      `${gate.errorBadge} ${
        gate.errorBadge === 1 ? "error" : "errors"
      } will be skipped`,
    );
  }
  if (gate.warningBadge !== undefined) {
    labelParts.push(
      `${gate.warningBadge} ${
        gate.warningBadge === 1 ? "warning" : "warnings"
      } will not block the run`,
    );
  }
  const ariaLabel =
    labelParts.length > 0
      ? `Run Scheduler. ${labelParts.join("; ")}.`
      : "Run Scheduler";

  return (
    <div className="flex items-center gap-2 ml-auto">
      <label htmlFor="run-time-limit" className="sr-only">
        Solver time limit
      </label>
      <select
        id="run-time-limit"
        data-testid="run-time-limit"
        value={timeLimitSeconds}
        onChange={(e) => setTimeLimitSeconds(Number(e.target.value))}
        title="How long the solver may search. Longer runs may find a better schedule."
        className="bg-surface-2 text-text-soft border border-border-subtle rounded-md px-2 py-1.5 text-[12px] cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand"
      >
        <option value={0}>10 min (default)</option>
        <option value={1800}>30 min</option>
        <option value={3600}>1 hour</option>
        <option value={14400}>4 hours</option>
        <option value={36000}>10 hours</option>
      </select>
      <button
        type="button"
        onClick={handleClick}
        data-testid="schedule-run-button"
        aria-label={ariaLabel}
        className="bg-brand hover:bg-brand-hover text-white rounded-md px-3 py-1.5 font-semibold text-[13px] flex items-center gap-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-page"
      >
        <RunIcon />
        Run Scheduler
        {gate.errorBadge !== undefined && (
          <span
            data-testid="schedule-run-error-badge"
            aria-hidden="true"
            className="ml-1 inline-flex items-center justify-center gap-0.5 min-w-[22px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold leading-none ring-1 ring-red-300/40"
          >
            {gate.errorBadge}
          </span>
        )}
        {gate.warningBadge !== undefined && (
          <span
            data-testid="schedule-run-warning-badge"
            aria-hidden="true"
            className="ml-1 inline-flex items-center justify-center gap-0.5 min-w-[22px] h-[18px] px-1 rounded-full bg-amber-400 text-amber-950 text-[11px] font-bold leading-none ring-1 ring-amber-200/40"
          >
            <WarningTriangleGlyph />
            {gate.warningBadge}
          </span>
        )}
      </button>
    </div>
  );
}

/**
 * Inline play-triangle icon used in both states so the button silhouette stays
 * consistent across the enabled / disabled transition.
 */
function RunIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M8 5v14l11-7-11-7Z" />
    </svg>
  );
}

/**
 * Indeterminate spinner glyph used while the mutation is in flight. CSS-only
 * spin (matches the rest of the design system's loading affordances).
 */
function SpinnerIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      aria-hidden="true"
      data-testid="schedule-run-spinner"
      className="animate-spin"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        opacity="0.25"
      />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Tiny triangle warning glyph rendered inside the amber count chip. Provides a
 * non-color signal so users who can't distinguish the amber background still
 * see a warning shape.
 */
function WarningTriangleGlyph() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      data-testid="schedule-run-warning-glyph"
    >
      <path d="M12 3 22 20H2L12 3Z" />
    </svg>
  );
}
